import { sortBy, uniqBy } from "lodash";
import { TDay } from "../ScrollableCalendarView";
import { v4 as uuid } from "uuid";

export interface CalendarColumnPosition {
  columnIndex: number;
  totalColumns: number;
}

type BasicCalendarEvent = {
  id: string;
  start: string;
  end: string;
};

type TypedCalendarEvent =
  | {
      type: "appointment";
      event: AppointmentMetaData;
    }
  | {
      type: "blocked-interval";
      event: CalendarBlockedInterval;
    }
  | {
      type: "gcal-event";
      event: CalendarBlockedInterval;
    }
  | {
      type: "group-session";
      event: GroupSessionMetaData;
    };

export type WrappedCalendarEvent = BasicCalendarEvent & TypedCalendarEvent;
export type WrappedCalendarEventWithColumnPosition = WrappedCalendarEvent & {
  calendarColumnPosition: CalendarColumnPosition;
};

interface OverlappingEventGroup {
  events: WrappedCalendarEvent[];
  start: string;
  end: string;
}

interface EventsByDay {
  fullDay: { [key in TDay]: WrappedCalendarEvent[] };
  nonFullDay: { [key in TDay]: WrappedCalendarEvent[][] };
}

export const wrapAppointment = (
  appointment: AppointmentMetaData,
): WrappedCalendarEvent => ({
  id: appointment.appointment_id.toString(),
  start: appointment.appointment_start,
  end: appointment.appointment_end,
  type: "appointment",
  event: appointment,
});

export const wrapBlockedInterval = (
  blockedInterval: CalendarBlockedInterval,
): WrappedCalendarEvent => ({
  id: blockedInterval.timeslot_id?.toString() || uuid(),
  start: blockedInterval.calendarStart || blockedInterval.start,
  end: blockedInterval.calendarEnd || blockedInterval.end,
  type: "blocked-interval",
  event: blockedInterval,
});

export const wrapGcalEvent = (
  gcalEvent: CalendarBlockedInterval,
): WrappedCalendarEvent => ({
  id: uuid(),
  start: gcalEvent.calendarStart || gcalEvent.start,
  end: gcalEvent.calendarEnd || gcalEvent.end,
  type: "gcal-event",
  event: gcalEvent,
});

export const wrapGroupSession = (
  groupSession: GroupSessionMetaData,
): WrappedCalendarEvent => ({
  id: groupSession.group_session_id.toString(),
  start: groupSession.group_session_start,
  end: groupSession.group_session_end,
  type: "group-session",
  event: groupSession,
});

const areGroupsOverlapping = (
  a: OverlappingEventGroup,
  b: OverlappingEventGroup,
) => a.start < b.end && a.end > b.start;

const mergeOverlappingGroups = (groups: OverlappingEventGroup[]) => ({
  events: groups.flatMap((group) => group.events),
  start: groups.map((group) => group.start).sort()[0],
  end: groups.map((group) => group.end).sort()[groups.length - 1],
});

const groupOverlappingEvents = (events: WrappedCalendarEvent[]) => {
  const initialGroups: OverlappingEventGroup[] = events.map((event) => ({
    start: event.start,
    end: event.end,
    events: [event],
  }));

  const mergedGroups: OverlappingEventGroup[] = [];

  initialGroups.forEach((group) => {
    const overlappingGroups = mergedGroups.filter((mergedGroup) =>
      areGroupsOverlapping(group, mergedGroup),
    );

    if (overlappingGroups.length === 0) {
      mergedGroups.push(group);
    } else {
      const mergedGroup = mergeOverlappingGroups([group, ...overlappingGroups]);

      mergedGroups.push(mergedGroup);

      overlappingGroups.forEach((group) => {
        const index = mergedGroups.indexOf(group);
        mergedGroups.splice(index, 1);
      });
    }
  });

  const mergedGroupswithAssignedColumns = mergedGroups.map((group) =>
    sortBy(uniqBy(group.events, "id"), "start").map((event, i, arr) => ({
      event,
      columnIndex: i,
      totalColumns: arr.length,
    })),
  );

  const eventColumnsById = mergedGroupswithAssignedColumns.flat().reduce(
    (acc, curr) => ({
      ...acc,
      [curr.event.id]: {
        columnIndex: curr.columnIndex,
        totalColumns: curr.totalColumns,
      },
    }),
    {} as {
      [key: string]: CalendarColumnPosition;
    },
  );

  return eventColumnsById;
};

/**
 * Extract provider ID from an event. Returns undefined for events without a provider.
 */
const getProviderId = (event: WrappedCalendarEvent): string | undefined => {
  if (event.type === "appointment") {
    return event.event.orchid_pro?.cognito_sub;
  }
  if (event.type === "blocked-interval") {
    return event.event.orchid_pro_user_id;
  }
  if (event.type === "gcal-event") {
    return event.event.orchid_pro_user_id;
  }
  return undefined;
};

const groupOverlappingEventsByProvider = (
  events: WrappedCalendarEvent[],
) => {
  // Group events by provider first
  const eventsByProvider = events.reduce(
    (acc, event) => {
      const providerId = getProviderId(event) ?? "__no_provider__";
      if (!acc[providerId]) {
        acc[providerId] = [];
      }
      acc[providerId].push(event);
      return acc;
    },
    {} as Record<string, WrappedCalendarEvent[]>,
  );

  // Apply the original function to each provider group separately
  const allEventColumnsById: Record<string, CalendarColumnPosition> = {};

  Object.values(eventsByProvider).forEach((providerEvents) => {
    const providerEventColumnsById = groupOverlappingEvents(providerEvents);
    Object.assign(allEventColumnsById, providerEventColumnsById);
  });

  return allEventColumnsById;
};

const addColumnPositionToEvents = (
  eventsByDay: EventsByDay,
  columnPositionById: {
    [key: string]: CalendarColumnPosition;
  },
): { [key in TDay]: WrappedCalendarEventWithColumnPosition[][] } => {
  const entries = Object.entries(eventsByDay.nonFullDay).map(([day, hours]) => [
    day,
    hours.map((hour) =>
      sortBy(
        hour.map((wrappedEvent) => ({
          ...wrappedEvent,
          calendarColumnPosition: columnPositionById[wrappedEvent.id],
        })),
        "calendarColumnPosition.columnIndex",
      ),
    ),
  ]);

  return Object.fromEntries(entries);
};

export const getNonFullDayEventsWithPosition = (eventsByDay: EventsByDay) => {
  const nonFullDayEvents = Object.values(eventsByDay.nonFullDay).flat(2);

  const columnPositionById = groupOverlappingEventsByProvider(
    nonFullDayEvents,
  );

  return addColumnPositionToEvents(eventsByDay, columnPositionById);
};
